// src/pages/FamilyOfficeDashboard.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import PortfolioReadinessCard from "../components/PortfolioReadinessCard";
import FamilyOfficeReportButton from "../components/FamilyOfficeReportButton";
import SalePreparation from "@/components/familyoffice/SalePreparation";
import {
  exportConsolidatedCsv,
  generateConsolidatedPdf,
} from "../utils/FamilyOfficeConsolidatedExport";
import {
  Users,
  AlertCircle,
  Search,
  RefreshCw,
  Briefcase,
  PieChart,
  ShieldCheck,
  FileText,
  ChevronRight,
  HeartHandshake,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  FolderOpen,
  Target,
  Scale,
  ArrowRight,
  Info,
  Download,
Landmark,
Shield,
TrendingUp,
Heart,
  Building2,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

export default function FamilyOfficeDashboard() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [collections, setCollections] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("value");
  const [activeModule, setActiveModule] = useState("overview");
  const [filterRisk, setFilterRisk] = useState("all");
  const [onlySuccession, setOnlySuccession] = useState(false);
  const [onlyPhilanthropy, setOnlyPhilanthropy] = useState(false);

  const [families, setFamilies] = useState([]);
  const [clientFamilyMap, setClientFamilyMap] = useState({});

  const [, setSuccessionPlans] = useState([]);
  const [, setPhilanthropy] = useState([]);

  const [showArtworksModal, setShowArtworksModal] = useState(false);
  const [modalArtworks, setModalArtworks] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSalePrep, setShowSalePrep] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportModalType, setReportModalType] = useState("overview");

const openSalePreparation = (artwork) => {
  setSelectedArtwork(artwork);
  setShowSalePrep(true);
};

  // Charger les familles avec le nombre de clients
  const loadFamilies = async () => {
    if (!user?.id) {
      console.error("❌ Pas d'utilisateur pour charger les familles");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("families")
        .select(`
          id,
          label,
          client_profile_id,
          family_office_id,
          status,
          notes,
          created_at,
          updated_at
        `)
        .eq("family_office_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("❌ Erreur chargement familles:", error);
        return;
      }

      if (data && data.length > 0) {
        const familiesWithClientCount = await Promise.all(
          data.map(async (f) => {
            const { data: familyClientsData, error: clientsError } = await supabase
              .from("family_clients")
              .select("id, client_id")
              .eq("family_id", f.id);

            if (clientsError) {
              console.error(`❌ Erreur comptage clients famille ${f.id}:`, clientsError);
            }

            const clientCount = familyClientsData?.length || 0;

            let client_name = null;
            if (f.client_profile_id) {
              try {
                const { data: profileData, error: profileErr } = await supabase
                  .from("profiles")
                  .select("first_name, last_name")
                  .eq("id", f.client_profile_id)
                  .single();

                if (!profileErr && profileData) {
                  client_name = `${profileData.first_name || ""} ${
                    profileData.last_name || ""
                  }`.trim();
                }
              } catch (err) {
                console.error(`❌ Erreur profil client pour famille ${f.id}:`, err);
              }
            }

            return {
              ...f,
              clientCount,
              client_name,
            };
          })
        );

        setFamilies(familiesWithClientCount);
        return familiesWithClientCount;
      } else {
        setFamilies([]);
        return [];
      }
    } catch (err) {
      console.error("❌ Erreur loadFamilies:", err);
      setFamilies([]);
      return [];
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reloadData = useCallback(async () => {
    if (!user?.id) {
      console.error("❌ Pas d'utilisateur connecté");
      return;
    }

    setLoading(true);
    try {
      // 1) Familles du FO
      const fams = await loadFamilies();
      const famIds = (fams || []).map((f) => f.id);

      // 2) Map client -> famille
      let map = {};
      if (famIds.length > 0) {
        const { data: links, error: linksErr } = await supabase
          .from("family_clients")
          .select("client_id, family_id")
          .in("family_id", famIds);

        if (linksErr) {
          console.error("❌ Erreur chargement family_clients:", linksErr);
        } else {
          (links || []).forEach((l) => {
            if (!l.client_id || !l.family_id) return;
            map[l.client_id] = l.family_id;
          });
        }
      }

      setClientFamilyMap(map);

      // 3) Permissions collections
      const { data: permissions, error: permError } = await supabase
        .from("collection_permissions")
        .select("collection_id, can_view, can_edit, client_id")
        .eq("grantee_id", user.id)
        .eq("can_view", true);

      if (permError) {
        console.error("❌ Erreur permissions:", permError);
        setLoading(false);
        return;
      }

      if (!permissions || permissions.length === 0) {
        setClients([]);
        setCollections([]);
        setArtworks([]);
        setLoading(false);
        return;
      }

      const clientIds = [
        ...new Set(permissions.map((p) => p.client_id).filter((id) => id != null)),
      ];

      if (clientIds.length === 0) {
        console.error("❌ Aucun client_id dans les permissions");
        setLoading(false);
        return;
      }

      let profiles = [];
      let profilesError = null;
      if (clientIds.length > 0) {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .in("id", clientIds);
        profilesError = error;
        profiles = data || [];
      }

      if (profilesError) {
        console.error("❌ Erreur chargement profils:", profilesError);
        setLoading(false);
        return;
      }

      const collectionIds = [
        ...new Set(permissions.map((p) => p.collection_id).filter((id) => id != null)),
      ];

      let collections = [];
      if (collectionIds.length > 0) {
        const { data, error } = await supabase
          .from("collections")
          .select("*")
          .in("id", collectionIds);
        if (error) {
          console.error("❌ Erreur chargement collections:", error);
        } else {
          collections = data || [];
        }
      }

      const artworkCollectionIds = collections.map((c) => c.id);
      let artworks = [];
      if (artworkCollectionIds.length > 0) {
        const { data } = await supabase
          .from("artworks_secure")
          .select("*")
          .in("collection_id", artworkCollectionIds);
        artworks = data || [];
      }

      // Succession : flag succession_plan_active posé par SuccessionPlanningPage
      // Mécénat : flag philanthropy_active posé par FamilyDetail
      // Les deux sont dans profiles déjà chargé via select("*")
      const clientsWithSuccessionFlag = new Set(
        (profiles || [])
          .filter((p) => p.succession_plan_active === true)
          .map((p) => p.id)
      );
      const clientsWithPhilanthropyFlag = new Set(
        (profiles || [])
          .filter((p) => p.philanthropy_active === true)
          .map((p) => p.id)
      );

      setSuccessionPlans([...clientsWithSuccessionFlag].map((id) => ({ client_id: id })));
      setPhilanthropy([...clientsWithPhilanthropyFlag].map((id) => ({ client_id: id })));

      const clientsList = (profiles || []).map((profile) => {
        const clientCollections = (collections || []).filter(
          (c) => c.client_id === profile.id
        );
        const clientCollectionIds = clientCollections.map((c) => c.id);
        const clientArtworks = (artworks || []).filter((a) =>
          clientCollectionIds.includes(a.collection_id)
        );

        let totalValue = 0;
        let purchaseValue = 0;
        clientArtworks.forEach((art) => {
          const currentValue = parseFloat(art.current_estimated_value) || parseFloat(art.valuation_estimated_value) || parseFloat(art.estimated_value) || parseFloat(art.value) || 0;
          totalValue += currentValue;
          purchaseValue += parseFloat(art.acquisition_price_eur) || parseFloat(art.acquisition_price) || parseFloat(art.price) || 0;
        });

        const growthRate =
          purchaseValue > 0 ? ((totalValue - purchaseValue) / purchaseValue) * 100 : 0;
        const riskLevel =
          totalValue > 1000000
            ? "high"
            : totalValue > 500000
            ? "medium"
            : "low";

        const hasSuccession = clientsWithSuccessionFlag.has(profile.id);
        const hasPhilanthropy = clientsWithPhilanthropyFlag.has(profile.id);

        return {
          id: profile.id,
          name: `${profile.first_name || ""} ${profile.last_name || ""}`.trim(),
          email: profile.email,
          totalValue,
          growthRate,
          riskLevel,
          collectionCount: clientCollections.length,
          artworkCount: clientArtworks.length,
          successionPlan: hasSuccession,
          philanthropy: hasPhilanthropy,
        };
      });

      setClients(clientsList);
      setCollections(collections);
      setArtworks(artworks);
      setLoading(false);
    } catch (err) {
      console.error("❌ Erreur reloadData:", err);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      reloadData();
    }
  }, [user, reloadData]);

  const [exporting, setExporting] = useState(false);

  // Récupère le profil du Family Office (branding des exports) une seule fois au clic
  const fetchFoProfile = useCallback(async () => {
    if (!user?.id) return {};
    const { data } = await supabase
      .from("profiles")
      .select("first_name, last_name, company")
      .eq("id", user.id)
      .maybeSingle();
    return data || {};
  }, [user?.id]);

  const handleExportCsv = useCallback(async () => {
    if (!artworks.length) {
      setErrorMessage("Aucune œuvre à exporter pour le moment.");
      setShowErrorModal(true);
      return;
    }
    setExporting(true);
    try {
      const foProfile = await fetchFoProfile();
      exportConsolidatedCsv({ foProfile, families, clientFamilyMap, artworks });
    } catch (e) {
      console.error("Export CSV consolidé:", e);
      setErrorMessage("Erreur lors de l'export CSV. Veuillez réessayer.");
      setShowErrorModal(true);
    } finally {
      setExporting(false);
    }
  }, [artworks, families, clientFamilyMap, fetchFoProfile]);

  const handleExportPdf = useCallback(async () => {
    if (!artworks.length) {
      setErrorMessage("Aucune œuvre à exporter pour le moment.");
      setShowErrorModal(true);
      return;
    }
    setExporting(true);
    try {
      const foProfile = await fetchFoProfile();
      generateConsolidatedPdf({ foProfile, families, clientFamilyMap, artworks });
    } catch (e) {
      console.error("Rapport PDF consolidé:", e);
      setErrorMessage("Erreur lors de la génération du rapport. Veuillez réessayer.");
      setShowErrorModal(true);
    } finally {
      setExporting(false);
    }
  }, [artworks, families, clientFamilyMap, fetchFoProfile]);

  const stats = useMemo(() => {
    const totalValue = clients.reduce((sum, c) => sum + (c.totalValue || 0), 0);
    const totalArtworks = clients.reduce((sum, c) => sum + (c.artworkCount || 0), 0);
    const avgGrowth =
      clients.length > 0
        ? clients.reduce((sum, c) => sum + (c.growthRate || 0), 0) / clients.length
        : 0;

    const clientsWithSuccession = clients.filter((c) => c.successionPlan).length;
    const clientsWithPhilanthropy = clients.filter((c) => c.philanthropy).length;

    const highRisk = clients.filter((c) => c.riskLevel === "high").length;
    const mediumRisk = clients.filter((c) => c.riskLevel === "medium").length;

    return {
      main: {
        totalClients: clients.length,
        totalValue,
        totalArtworks,
        avgGrowth,
        clientsWithSuccession,
        clientsWithPhilanthropy,
      },
      risk: {
        high: highRisk,
        medium: mediumRisk,
        low: clients.length - highRisk - mediumRisk,
      },
    };
  }, [clients]);


  // ── Vision IA stats — calculées depuis artworks déjà chargés (select("*"))
  // Les colonnes IA sont écrites par vision.artkiv.com dans la même table artworks
  const visionStats = useMemo(() => {
    if (!artworks || artworks.length === 0) return null;

    const analysed = artworks.filter((a) => a.style_ai || a.visual_analysis || a.valuation_estimated_value);
    const withConservation = artworks.filter((a) => a.condition_report && a.condition_report.trim() !== "");
    const withValuation = artworks.filter((a) => a.valuation_estimated_value || a.valuation_consensus);
    const withBlockchain = artworks.filter((a) => a.certificate_blockchain === true);

    const atRiskConservation = artworks.filter(
      (a) => a.condition_report && !a.last_valuation_date
    );

    const styleMap = {};
    artworks.forEach((a) => {
      if (a.style_ai) {
        styleMap[a.style_ai] = (styleMap[a.style_ai] || 0) + 1;
      }
    });
    const topStyles = Object.entries(styleMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([style, count]) => ({ style, count }));

    const visionTotalValue = withValuation.reduce((sum, a) => {
      return sum + (parseFloat(a.valuation_consensus) || parseFloat(a.valuation_estimated_value) || 0);
    }, 0);

    const avgConfidence = withValuation.length > 0
      ? withValuation.reduce((sum, a) => sum + (parseFloat(a.valuation_confidence_score) || 0), 0) / withValuation.length
      : 0;

    return {
      total: artworks.length,
      analysed: analysed.length,
      analysedPct: artworks.length > 0 ? Math.round((analysed.length / artworks.length) * 100) : 0,
      withConservation: withConservation.length,
      withValuation: withValuation.length,
      withBlockchain: withBlockchain.length,
      atRiskConservation: atRiskConservation.length,
      topStyles,
      visionTotalValue,
      avgConfidence: Math.round(avgConfidence * 100) / 100,
    };
  }, [artworks]);

  const filteredClients = useMemo(() => {
    let result = [...clients];

    if (searchQuery) {
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (c.email && c.email.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (filterRisk !== "all") {
      result = result.filter((c) => c.riskLevel === filterRisk);
    }

    if (onlySuccession) {
      result = result.filter((c) => c.successionPlan);
    }

    if (onlyPhilanthropy) {
      result = result.filter((c) => c.philanthropy);
    }

    if (sortBy === "value") {
      result.sort((a, b) => (b.totalValue || 0) - (a.totalValue || 0));
    } else if (sortBy === "risk") {
      const riskOrder = { high: 1, medium: 2, low: 3 };
      result.sort((a, b) => riskOrder[a.riskLevel] - riskOrder[b.riskLevel]);
    }

    return result;
  }, [clients, searchQuery, sortBy, filterRisk, onlySuccession, onlyPhilanthropy]);

  const goToFamily = (client) => {
    const familyId = clientFamilyMap[client.id];
    if (familyId) {
      navigate(`/family-office/families/${familyId}`);
    } else {
      setErrorMessage(
        `Aucune famille trouvée pour ${client.name}. Vérifiez family_clients.`
      );
      setShowErrorModal(true);
    }
  };

  const openArtworksForClient = (client) => {
    const clientCollections = collections.filter((c) => c.client_id === client.id);
    const clientCollectionIds = clientCollections.map((c) => c.id);
    const clientArtworks = artworks.filter((a) =>
      clientCollectionIds.includes(a.collection_id)
    );
    setModalArtworks(clientArtworks);
    setShowArtworksModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-[#1A1A1A]" size={32} />
          <p className="text-sm text-[#666]">Chargement de vos données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <DashboardHeader />
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                Familles suivies
              </p>
              <Users size={16} className="text-[#666]" />
            </div>
            <p className="text-2xl font-light text-[#1A1A1A] mb-1">
              {families.length}
            </p>
            <p className="text-xs text-[#666]">
              {stats.main.totalClients} client{stats.main.totalClients > 1 ? "s" : ""}
            </p>
          </div>

          <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                Encours global
              </p>
              <BarChart3 size={16} className="text-[#666]" />
            </div>
            <p className="text-2xl font-light text-[#1A1A1A] mb-1">
              {stats.main.totalValue
                ? new Intl.NumberFormat("fr-FR", {
                    style: "currency",
                    currency: "EUR",
                    notation: "compact",
                  }).format(stats.main.totalValue)
                : "—"}
            </p>
            <p className="text-xs text-[#666]">{stats.main.totalArtworks} œuvres</p>
          </div>

          <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                Performance moyenne
              </p>
              {stats.main.avgGrowth > 0 ? (
                <ArrowUpRight size={16} className="text-emerald-600" />
              ) : (
                <ArrowDownRight size={16} className="text-red-600" />
              )}
            </div>
            <p
              className={`text-2xl font-light mb-1 ${
                stats.main.avgGrowth > 0 ? "text-emerald-600" : "text-red-600"
              }`}
            >
              {stats.main.avgGrowth > 0 ? "+" : ""}
              {stats.main.avgGrowth.toFixed(1)}%
            </p>
            <p className="text-xs text-[#666]">vs acquisition</p>
          </div>

          <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                Niveau de risque
              </p>
              <AlertCircle size={16} className="text-[#666]" />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-xs text-[#666]">{stats.risk.high}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-xs text-[#666]">{stats.risk.medium}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-xs text-[#666]">{stats.risk.low}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white border border-[#E8E8E8] rounded p-4 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  id="succession-filter"
                  checked={onlySuccession}
                  onChange={(e) => setOnlySuccession(e.target.checked)}
                  className="w-3 h-3"
                />
                <label htmlFor="succession-filter" className="text-[#666]">
                  Succession structurée
                </label>
              </div>
              <div className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  id="philanthropy-filter"
                  checked={onlyPhilanthropy}
                  onChange={(e) => setOnlyPhilanthropy(e.target.checked)}
                  className="w-3 h-3"
                />
                <label htmlFor="philanthropy-filter" className="text-[#666]">
                  Programme mécénat
                </label>
              </div>
              <select
                value={filterRisk}
                onChange={(e) => setFilterRisk(e.target.value)}
                className="px-2 py-1 text-xs border border-[#E8E8E8] rounded"
              >
                <option value="all">Tous niveaux de risque</option>
                <option value="high">Risque élevé</option>
                <option value="medium">Risque moyen</option>
                <option value="low">Risque faible</option>
              </select>
            </div>

            <div className="relative flex-shrink-0">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-[#999]" size={14} />
              <input
                type="text"
                placeholder="Rechercher un client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-3 py-2 text-xs border border-[#E8E8E8] rounded w-full sm:w-64"
              />
            </div>

            {/* Exports consolidés multi-familles */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={exporting}
                title="Export CSV de toutes les œuvres, toutes familles (compatible ERP / IFI)"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-[#E8E8E8] rounded hover:bg-[#FAFAF8] disabled:opacity-50"
              >
                <FileText size={14} /> Export CSV
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={exporting}
                title="Rapport patrimonial consolidé (PDF)"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-[#1A1A1A] text-white rounded hover:bg-[#333] disabled:opacity-50"
              >
                <FileText size={14} /> {exporting ? "Génération…" : "Rapport PDF"}
              </button>
            </div>
          </div>
        </div>

        {/* POINTS À TRAITER CE TRIMESTRE - DÉPLACÉ ICI */}
        <div className="bg-white border border-[#E8E8E8] rounded-xl p-6 shadow-sm mb-10">
          <div className="mb-6">
            <h2 className="text-lg font-light text-[#1A1A1A] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Points à traiter ce trimestre
            </h2>
            <div className="h-0.5 w-16 bg-[#C9A961] rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.main.totalClients - stats.main.clientsWithSuccession > 0 && (
              <div className="group">
                <div className="flex flex-col h-full">
                  <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-white font-medium text-base shadow-md mb-3">
                    1
                  </div>
                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">
                    Démarchage & succession
                  </h4>
                  <p className="text-xs text-[#666] mb-3 leading-relaxed flex-1">
                    {stats.main.totalClients - stats.main.clientsWithSuccession} client(s) sans plan structuré. 
                    Prioriser un cadrage avec notaire/avocat.
                  </p>
                  <Link
                    to="/estimation-succession"
                    className="inline-flex items-center gap-1.5 text-xs text-[#1A1A1A] hover:text-[#666] font-medium group-hover:gap-2 transition-all"
                  >
                    Préparer un rapport →
                  </Link>
                </div>
              </div>
            )}

            <div className="group">
              <div className="flex flex-col h-full">
                <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-white font-medium text-base shadow-md mb-3">
                  2
                </div>
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">
                  Campagne de valorisation
                </h4>
                <p className="text-xs text-[#666] mb-3 leading-relaxed flex-1">
                  Cibler les familles dont les valorisations datent de +24 mois.
                </p>
                <Link
                  to="/family-office/market-valuations"
                  className="inline-flex items-center gap-1.5 text-xs text-[#1A1A1A] hover:text-[#666] font-medium group-hover:gap-2 transition-all"
                >
                  Lancer les valorisations →
                </Link>
              </div>
            </div>

            <div className="group">
              <div className="flex flex-col h-full">
                <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-white font-medium text-base shadow-md mb-3">
                  3
                </div>
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">
                  Mécénat & philanthropie
                </h4>
                <p className="text-xs text-[#666] mb-3 leading-relaxed flex-1">
                  Stratégies de mécénat et de don pour le client actif.
                </p>
                <Link
                  to="/family-office/philanthropie"
                  className="inline-flex items-center gap-1.5 text-xs text-[#1A1A1A] hover:text-[#666] font-medium group-hover:gap-2 transition-all"
                >
                  Ouvrir le conseil philanthropie →
                </Link>
              </div>
            </div>

            <div className="group">
              <div className="flex flex-col h-full">
                <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-white font-medium text-base shadow-md mb-3">
                  3
                </div>
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">
                  Opportunités de prêts
                </h4>
                <p className="text-xs text-[#666] mb-3 leading-relaxed flex-1">
                  Identifier les œuvres prêtables pour expositions 2025–2026.
                </p>
                <Link
                  to="/family-office/en-developpement?module=museum-loans"
                  className="inline-flex items-center gap-1.5 text-xs text-[#1A1A1A] hover:text-[#666] font-medium group-hover:gap-2 transition-all"
                >
                  Explorer les institutions →
                </Link>
              </div>
            </div>

            <div className="group">
              <div className="flex flex-col h-full">
                <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-white font-medium text-base shadow-md mb-3">
                  4
                </div>
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">
                  Maisons de vente
                </h4>
                <p className="text-xs text-[#666] mb-3 leading-relaxed flex-1">
                  Suivre les ventes pertinentes (comparables, acquisitions).
                </p>
                <Link
                  to="/family-office/en-developpement?module=auction-houses"
                  className="inline-flex items-center gap-1.5 text-xs text-[#1A1A1A] hover:text-[#666] font-medium group-hover:gap-2 transition-all"
                >
                  Suivre les ventes →
                </Link>
              </div>
            </div>
          </div>
        </div>


        {/* ── Vision IA — Intelligence collection ── */}
        {visionStats && (
          <div className="bg-white border border-[#E8E8E8] rounded-xl p-6 shadow-sm mb-10">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-xs font-medium text-[#C9A961] tracking-widest uppercase mb-1">
                  Artkiv Vision IA
                </p>
                <h2 className="text-lg font-light text-[#1A1A1A]" style={{ fontFamily: "Georgia, serif" }}>
                  Intelligence collection
                </h2>
              </div>
              <a
                href="https://vision.artkiv.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#C9A961] border border-[#C9A961] px-3 py-1.5 rounded hover:bg-[#C9A961] hover:text-white transition-colors"
              >
                Ouvrir Vision IA ↗
              </a>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-[#F9F7F4] rounded-lg p-4">
                <p className="text-[10px] text-[#999] uppercase tracking-wide mb-1">Analyses IA</p>
                <p className="text-2xl font-light text-[#1A1A1A]">{visionStats.analysedPct}%</p>
                <p className="text-xs text-[#666]">{visionStats.analysed} / {visionStats.total} oeuvres</p>
              </div>
              <div className="bg-[#F9F7F4] rounded-lg p-4">
                <p className="text-[10px] text-[#999] uppercase tracking-wide mb-1">Rapports conservation</p>
                <p className="text-2xl font-light text-[#1A1A1A]">{visionStats.withConservation}</p>
                <p className="text-xs text-[#666]">
                  {visionStats.atRiskConservation > 0 ? (
                    <span className="text-amber-600">{visionStats.atRiskConservation} à revisiter</span>
                  ) : (
                    <span className="text-emerald-600">Tout à jour</span>
                  )}
                </p>
              </div>
              <div className="bg-[#F9F7F4] rounded-lg p-4">
                <p className="text-[10px] text-[#999] uppercase tracking-wide mb-1">Valorisations IA</p>
                <p className="text-2xl font-light text-[#1A1A1A]">{visionStats.withValuation}</p>
                <p className="text-xs text-[#666]">
                  {visionStats.visionTotalValue > 0
                    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", notation: "compact" }).format(visionStats.visionTotalValue)
                    : "—"}
                  {visionStats.avgConfidence > 0 && (
                    <span className="text-[#999] ml-1">· conf. {visionStats.avgConfidence}</span>
                  )}
                </p>
              </div>
              <div className="bg-[#F9F7F4] rounded-lg p-4">
                <p className="text-[10px] text-[#999] uppercase tracking-wide mb-1">Blockchain</p>
                <p className="text-2xl font-light text-[#1A1A1A]">{visionStats.withBlockchain}</p>
                <p className="text-xs text-[#666]">certificats émis</p>
              </div>
            </div>

            {/* Styles dominants */}
            {visionStats.topStyles.length > 0 && (
              <div>
                <p className="text-[10px] text-[#999] uppercase tracking-wide mb-3">Styles dominants (IA)</p>
                <div className="flex flex-wrap gap-2">
                  {visionStats.topStyles.map(({ style, count }) => (
                    <span
                      key={style}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F9F7F4] border border-[#E8E0D0] rounded-full text-xs text-[#444]"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C9A961] inline-block" />
                      {style}
                      <span className="text-[#999]">({count})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Alerte si peu d'oeuvres analysées */}
            {visionStats.analysedPct < 50 && visionStats.total > 0 && (
              <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Info size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  Moins de la moitié des oeuvres ont été analysées par Vision IA.{" "}
                  <a href="https://vision.artkiv.com" target="_blank" rel="noopener noreferrer" className="underline">
                    Lancer les analyses
                  </a>{" "}
                  pour enrichir les données de vos familles clientes.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Portfolio Readiness (§9.3) — préparation patrimoniale par famille, moteur commun */}
        <div className="mb-10">
          <div className="mb-3 flex justify-end">
            <FamilyOfficeReportButton
              clients={clients}
              foName={`${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() || profile?.company || "Family Office"}
            />
          </div>
          <PortfolioReadinessCard clients={clients} />
        </div>

        {/* Vue liste des clients + colonne droite */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                  Portefeuille clients & familles
                </p>
                <h3 className="text-lg font-light text-[#1A1A1A]">
                  {filteredClients.length} client
                  {filteredClients.length > 1 ? "s" : ""} suivi
                  {filteredClients.length > 1 ? "s" : ""}
                </h3>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <button
                  onClick={() => setSortBy("value")}
                  className={`px-2 py-1 rounded border ${
                    sortBy === "value"
                      ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                      : "bg-white border-[#E8E8E8] text-[#666]"
                  }`}
                >
                  Par encours
                </button>
                <button
                  onClick={() => setSortBy("risk")}
                  className={`px-2 py-1 rounded border ${
                    sortBy === "risk"
                      ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                      : "bg-white border-[#E8E8E8] text-[#666]"
                  }`}
                >
                  Par risque
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  className="bg-white border border-[#E8E8E8] rounded p-4 hover:border-[#1A1A1A] hover:shadow-sm transition-all flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-[#1A1A1A] truncate">
                        {client.name}
                      </p>
                      {client.successionPlan && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-100">
                          Succession
                        </span>
                      )}
                      {client.philanthropy && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-100">
                          Mécénat
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#666] mb-1 truncate">
                      {client.email || "Contact non renseigné"}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#999]">
                      <span className="flex items-center gap-1">
                        <FolderOpen size={11} />
                        <span>
                          {client.collectionCount || 0} collection
                          {(client.collectionCount || 0) > 1 ? "s" : ""}
                        </span>
                      </span>
                      <span>•</span>
                      <span>
                        {client.artworkCount || 0} œuvre
                        {(client.artworkCount || 0) > 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 min-w-[170px]">
                    <div className="text-right">
                      <p className="text-xs text-[#999]">Encours estimé</p>
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        {client.totalValue
                          ? new Intl.NumberFormat("fr-FR", {
                              style: "currency",
                              currency: "EUR",
                              notation: "compact",
                            }).format(client.totalValue)
                          : "—"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      {client.growthRate > 0 ? (
                        <>
                          <ArrowUpRight size={12} className="text-emerald-600" />
                          <span className="text-emerald-600">
                            +{client.growthRate.toFixed(1)}%
                          </span>
                        </>
                      ) : client.growthRate < 0 ? (
                        <>
                          <ArrowDownRight size={12} className="text-red-600" />
                          <span className="text-red-600">
                            {client.growthRate.toFixed(1)}%
                          </span>
                        </>
                      ) : (
                        <span className="text-[#999]">0,0%</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-1">
                      <button
                        onClick={() => goToFamily(client)}
                        className="text-xs text-[#1A1A1A] flex items-center gap-1 hover:underline"
                      >
                        Voir famille
                        <ChevronRight size={12} />
                      </button>
                      <button
                        onClick={() => openArtworksForClient(client)}
                        className="text-xs text-[#666] hover:text-[#1A1A1A] underline hover:no-underline"
                        title="Voir toutes les œuvres gérées de ce client"
                      >
                        Œuvres gérées
                      </button>
                      <button
                        onClick={() => navigate(`/succession?clientId=${client.id}`)}
                        className="text-xs text-[#666] hover:text-[#1A1A1A] underline hover:no-underline"
                        title="Consulter et compléter le dossier de transmission de ce client"
                      >
                        Plan de transmission
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Colonne droite : modules experts + actions structurantes fusionnés */}
          <div className="space-y-6">

            {/* ── RAPPORTS ── */}
            <div className="bg-white border border-[#E8E8E8] rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs font-medium text-[#C9A961] tracking-widest uppercase mb-1">Artkiv</p>
                  <h3 className="text-base font-light text-[#1A1A1A]" style={{ fontFamily: "Georgia, serif" }}>
                    Rapports patrimoniaux
                  </h3>
                </div>
                <FileText size={18} className="text-[#C9A961]" />
              </div>
              <p className="text-xs text-[#666] mb-4 leading-relaxed">
                Générez un rapport PDF pour une famille — inventaire, conservation IA,
                héritiers, blockchain, données successorales.
              </p>
              <div className="space-y-2">
                {[
                  { type: "overview",     label: "Rapport patrimonial",  sub: "Vue complète de la collection" },
                  { type: "succession",   label: "Plan de transmission",  sub: "Héritiers, matching, données notaire" },
                  { type: "philanthropy", label: "Stratégie de mécénat", sub: "Leviers fiscaux et engagements" },
                ].map(({ type, label, sub }) => (
                  <button
                    key={type}
                    onClick={() => { setReportModalType(type); setShowReportModal(true); }}
                    className="w-full text-left border border-[#E8E8E8] rounded-lg px-4 py-3 hover:border-[#C9A961] hover:bg-[#F9F7F4] transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#C9A961] transition-colors">{label}</p>
                        <p className="text-xs text-[#999] mt-0.5">{sub}</p>
                      </div>
                      <Download size={14} className="text-[#CCC] group-hover:text-[#C9A961] transition-colors flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#BBB] mt-3">
                Le rapport est téléchargé depuis la fiche famille.
              </p>
            </div>

            {/* MODULES EXPERTS & ACTIONS STRUCTURANTES */}
            <div className="bg-white border border-[#E8E8E8] rounded-xl shadow-sm p-6">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-[#F9F7F4] border border-[#E8E0D0] flex items-center justify-center flex-shrink-0">
                  <Target className="text-[#C9A961]" size={18} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-medium text-[#C9A961] tracking-widest uppercase mb-1">Artkiv</p>
                  <h3 className="text-base font-light text-[#1A1A1A]" style={{ fontFamily: 'Georgia, serif' }}>
                    Modules experts & Actions
                  </h3>
                  <p className="text-xs text-[#999] mt-0.5">
                    3 chantiers prioritaires et modules spécialisés
                  </p>
                </div>
              </div>

              {/* Actions cliquables vers modules */}
              <div className="space-y-2 mb-5">
                <button
                  onClick={() => setActiveModule("succession")}
                  className="w-full bg-[#F9F7F4] border border-[#E8E0D0] rounded-lg p-4 hover:border-[#C9A961] hover:bg-[#FBF9F5] transition-all group text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-white border border-[#E8E0D0] flex items-center justify-center flex-shrink-0">
                      <Scale className="text-[#C9A961]" size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#C9A961] transition-colors">
                          Sécurisation juridique & fiscale
                        </h4>
                        <ChevronRight className="text-[#CCC] group-hover:text-[#C9A961] transition-colors" size={14} />
                      </div>
                      <p className="text-xs text-[#999] leading-relaxed">
                        {stats.main.clientsWithSuccession} dossiers avec plans de succession structurés
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setActiveModule("insurance")}
                  className="w-full bg-[#F9F7F4] border border-[#E8E0D0] rounded-lg p-4 hover:border-[#C9A961] hover:bg-[#FBF9F5] transition-all group text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-white border border-[#E8E0D0] flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="text-[#C9A961]" size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#C9A961] transition-colors">
                          Assurance & gestion des risques
                        </h4>
                        <ChevronRight className="text-[#CCC] group-hover:text-[#C9A961] transition-colors" size={14} />
                      </div>
                      <p className="text-xs text-[#999] leading-relaxed">
                        Polices, garanties et zones de vulnérabilité
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setActiveModule("philanthropy")}
                  className="w-full bg-[#F9F7F4] border border-[#E8E0D0] rounded-lg p-4 hover:border-[#C9A961] hover:bg-[#FBF9F5] transition-all group text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-white border border-[#E8E0D0] flex items-center justify-center flex-shrink-0">
                      <HeartHandshake className="text-[#C9A961]" size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#C9A961] transition-colors">
                          Philanthropie & mécénat
                        </h4>
                        <ChevronRight className="text-[#CCC] group-hover:text-[#C9A961] transition-colors" size={14} />
                      </div>
                      <p className="text-xs text-[#999] leading-relaxed">
                        Dons, prêts, fondations et défiscalisation
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Séparateur */}
              <div className="h-px bg-[#E8E8E8] my-4"></div>

              {/* Actions complémentaires */}
              <div className="space-y-1.5">
                <p className="text-[10px] text-[#BBB] uppercase tracking-widest mb-3">Chantiers prioritaires</p>

                <div className="flex items-center gap-3 px-3 py-2 rounded bg-[#F9F7F4] hover:bg-[#F5F0E8] transition-colors">
                  <Landmark className="text-[#C9A961] flex-shrink-0" size={13} />
                  <span className="text-xs text-[#555]">Prêts & visibilité institutionnelle</span>
                </div>

                <div className="flex items-center gap-3 px-3 py-2 rounded bg-[#F9F7F4] hover:bg-[#F5F0E8] transition-colors">
                  <Building2 className="text-[#C9A961] flex-shrink-0" size={13} />
                  <span className="text-xs text-[#555]">Opportunités maisons de vente</span>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Section modules détaillés */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne principale module actif */}
          <div className="lg:col-span-2 space-y-4">
            {activeModule === "succession" && (
              <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                      Module Succession & Transmission
                    </p>
                    <h3 className="text-lg font-light text-[#1A1A1A]">
                      Simulations successorales & scénarios de répartition
                    </h3>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#666]">
                    <FileText size={14} />
                    <span>
                      {stats.main.clientsWithSuccession} client
                      {stats.main.clientsWithSuccession > 1 ? "s" : ""} avec dossier
                      structuré
                    </span>
                  </div>
                </div>

                <p className="text-xs text-[#666] mb-4">
                  Pour chaque famille, Artkiv agrège les valorisations, la structure de
                  détention (pleine propriété, démembrement, sociétés), les préférences
                  fiscales et les bénéficiaires pressentis.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">Scénarios simulables</p>
                    <p className="text-sm font-medium text-[#1A1A1A]">
                      Donation, démembrement, legs, réserve
                    </p>
                  </div>
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">Paramètres pris en compte</p>
                    <p className="text-sm text-[#666]">
                      Régime matrimonial, pays de résidence, nature des actifs
                    </p>
                  </div>
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">Format de sortie</p>
                    <p className="text-sm text-[#666]">
                      Rapport synthétique pour le client + annexes pour les avocats /
                      notaires
                    </p>
                  </div>
                </div>

                {/* Focus planification fiscale -> ComingSoon */}
                <div className="border border-[#E8E8E8] rounded-lg p-3 bg-[#FCFCFB]">
                  <p className="text-[11px] text-[#999] mb-1">
                    Planification fiscale détaillée
                  </p>
                  <p className="text-xs text-[#666] mb-2">
                    Optimisation des flux de donation / vente et simulations d&apos;imposition
                    par pays et par scénario.
                  </p>
                  <Link
                    to="/family-office/en-developpement?module=fiscal-optimization"
                    className="inline-flex items-center gap-1 text-[11px] text-[#C9A961] hover:text-[#1A1A1A] transition-colors"
                  >
                    En cours de développement →
                  </Link>
                </div>
              </div>
            )}

            {activeModule === "insurance" && (
              <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                      Module Assurance & Risques
                    </p>
                    <h3 className="text-lg font-light text-[#1A1A1A]">
                      Suivi des polices, garanties et zones de vulnérabilité
                    </h3>
                  </div>
                </div>

                <p className="text-xs text-[#666] mb-4">
                  L&apos;outil consolide les polices d&apos;assurance, les garanties et les
                  exclusions, en les confrontant aux estimations d&apos;Artkiv et à la réalité
                  des mouvements d&apos;œuvres.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">Couverture globale</p>
                    <p className="text-sm text-[#1A1A1A]">
                      Taux de couverture vs valorisation totale
                    </p>
                  </div>
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">Analyse des exclusions</p>
                    <p className="text-sm text-[#666]">
                      Conditions spécifiques, transport, prêt, événements
                    </p>
                  </div>
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">Alertes</p>
                    <p className="text-sm text-[#666]">
                      Renouvellements, sous-assurance, zones géographiques sensibles
                    </p>
                  </div>
                </div>

                <div className="border border-[#E8E8E8] rounded-lg p-3 bg-[#FAFAF8]">
                  <p className="text-[11px] text-[#999] mb-1">
                    Module Assurance complet
                  </p>
                  <p className="text-xs text-[#666] mb-2">
                    Audit des polices, simulation d&apos;ajustement de garanties et
                    scénarios de sous/sur-assurance.
                  </p>
                  <Link
                    to="/family-office/en-developpement?module=insurance"
                    className="inline-flex items-center gap-1 text-[11px] text-[#C9A961] hover:text-[#1A1A1A] transition-colors"
                  >
                    En cours de développement →
                  </Link>
                </div>
              </div>
            )}

            {activeModule === "philanthropy" && (
              <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                      Module Philanthropie & Mécénat
                    </p>
                    <h3 className="text-lg font-light text-[#1A1A1A]">
                      Dons, prêts, fondations, mécénat de compétences
                    </h3>
                  </div>
                </div>

                <p className="text-xs text-[#666] mb-4">
                  Suivi des engagements, des dons passés, des promesses de dons et des
                  opportunités de mécénat cohérentes avec l&apos;histoire de chaque famille.
                </p>

                {/* 7 actions de mécénat culturel */}
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-1">
                  🎨 7 Actions de Mécénat Culturel
                </h4>
                <p className="text-xs text-[#666] mb-4">
                  Accompagnez vos familles dans la valorisation et la transmission de leurs
                  collections.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 1. Prêts aux institutions */}
                  <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <Landmark className="w-4 h-4 text-blue-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        Prêts aux institutions
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      Exposer temporairement des œuvres dans des musées prestigieux.
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      💰 66% déduction • 3–36 mois
                    </p>
                  </div>

                  {/* 2. Expositions publiques */}
                  <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <PieChart className="w-4 h-4 text-amber-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        Expositions publiques
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      Organiser des expositions temporaires de la collection.
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      ✨ Frais déductibles • Valorisation
                    </p>
                  </div>

                  {/* 3. Conservation & restauration */}
                  <div className="border border-teal-200 bg-teal-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-teal-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        Conservation & restauration
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      Restaurer, documenter et numériser les œuvres de la collection.
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      🛡️ Déduction partielle • Préservation
                    </p>
                  </div>

                  {/* 4. Location aux entreprises */}
                  <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <Briefcase className="w-4 h-4 text-emerald-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        Location aux entreprises
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      Louer des œuvres pour la décoration d&apos;espaces professionnels.
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      💵 Revenus défiscalisables • 1–5 ans
                    </p>
                  </div>

                  {/* 5. Donation avec usufruit */}
                  <div className="border border-purple-200 bg-purple-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-purple-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        Donation avec usufruit
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      Donner la propriété tout en conservant l&apos;usage viager des œuvres.
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      🎁 66% + 0% succession • Viager
                    </p>
                  </div>

                  {/* 6. Actions éducatives */}
                  <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-indigo-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        Actions éducatives
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      Visites scolaires, ateliers pédagogiques autour de la collection.
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      📚 60% déduction • Impact social
                    </p>
                  </div>

                  {/* 7. Achat pour donation */}
                  <div className="border border-rose-200 bg-rose-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <Heart className="w-4 h-4 text-rose-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        Achat pour donation
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      Acquérir des œuvres complémentaires pour ensuite les donner.
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      🎯 66% de l&apos;achat • Enrichissement
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ========== COLONNE DROITE - PRIORISATION ========== */}
          <div className="space-y-6">
            {/* PRIORISATION */}
            <div className="bg-white border border-[#E8E8E8] rounded-xl shadow-sm p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#F9F7F4] border border-[#E8E0D0] flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="text-[#C9A961]" size={18} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-medium text-[#C9A961] tracking-widest uppercase mb-1">Artkiv</p>
                  <h3 className="text-base font-light text-[#1A1A1A]" style={{ fontFamily: 'Georgia, serif' }}>
                    Priorisation accompagnement
                  </h3>
                  <p className="text-xs text-[#999] mt-1 leading-relaxed">
                    Générez une liste d'actions priorisées par famille, en fonction des risques, opportunités fiscales et contraintes calendaires.
                  </p>
                </div>
              </div>
              <button className="w-full bg-[#1A1A1A] hover:bg-[#333] text-white rounded px-4 py-3 text-sm font-medium transition-all flex items-center justify-between group">
                <span>Lancer une revue trimestrielle</span>
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showArtworksModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-light text-[#1A1A1A]" style={{ fontFamily: 'Georgia, serif' }}>
                Œuvres gérées
              </h2>
              <button
                onClick={() => setShowArtworksModal(false)}
                className="text-[#666] hover:text-[#1A1A1A] text-2xl leading-none"
              >
                ✕
              </button>
            </div>
            
            {modalArtworks.length === 0 ? (
              <p className="text-sm text-[#666] text-center py-8">
                Aucune œuvre trouvée pour ce client.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-[#999] border-b border-[#E8E8E8]">
                    <tr>
                      <th className="pb-3 pr-4 font-medium">Titre</th>
                      <th className="pb-3 pr-4 font-medium">Artiste</th>
                      <th className="pb-3 pr-4 font-medium">Valeur estimée</th>
                      <th className="pb-3 pr-4 font-medium">Localisation</th>
                      <th className="pb-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalArtworks.map((art) => (
                      <tr key={art.id} className="border-b border-[#E8E8E8] hover:bg-[#FAFAFA] transition-colors">
                        <td className="py-3 pr-4">
                          <span className="text-[#1A1A1A] font-medium">
                            {art.title || "Sans titre"}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-[#666]">
                          {art.artist || "Artiste inconnu"}
                        </td>
                        <td className="py-3 pr-4 text-[#1A1A1A]">
                          {art.estimated_value 
                            ? new Intl.NumberFormat("fr-FR", {
                                style: "currency",
                                currency: "EUR",
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                              }).format(art.estimated_value)
                            : "—"}
                        </td>
                        <td className="py-3 pr-4 text-[#666]">
                          {art.location || "—"}
                        </td>
                        <td className="py-3 flex flex-col gap-1">
  <button
    onClick={() => openSalePreparation(art)}
    className="text-xs text-blue-600 underline hover:no-underline font-medium"
  >
    Mise en vente
  </button>
</td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {showErrorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-lg font-medium text-[#1A1A1A] mb-2">Erreur</h2>
            <p className="text-sm text-[#666] mb-4">{errorMessage}</p>
            <button
              onClick={() => setShowErrorModal(false)}
              className="w-full bg-[#1A1A1A] text-white rounded py-2 text-sm hover:bg-[#333]"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {showSalePrep && selectedArtwork && (
        <SalePreparation
          artwork={selectedArtwork}
          onClose={() => {
            setShowSalePrep(false);
            setSelectedArtwork(null);
          }}
        />
      )}
      {/* ── Modal sélection famille pour rapport ── */}
      {showReportModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowReportModal(false)}
        >
          <div
            className="bg-white rounded-xl max-w-md w-full shadow-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-xs text-[#C9A961] uppercase tracking-widest mb-1">Artkiv</p>
                <h3 className="text-lg font-light text-[#1A1A1A]" style={{ fontFamily: "Georgia, serif" }}>
                  {{ overview: "Rapport patrimonial", succession: "Plan de transmission", philanthropy: "Stratégie de mécénat" }[reportModalType]}
                </h3>
              </div>
              <button onClick={() => setShowReportModal(false)} className="text-[#999] hover:text-[#1A1A1A] text-xl leading-none">✕</button>
            </div>
            <p className="text-sm text-[#666] mb-5">
              Sélectionnez la famille pour laquelle générer le rapport.
              Vous serez redirigé(e) vers sa fiche où le PDF peut être téléchargé.
            </p>
            {families.length === 0 ? (
              <p className="text-sm text-[#999] text-center py-4">Aucune famille disponible.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {families.map((fam) => (
                  <button
                    key={fam.id}
                    onClick={() => { setShowReportModal(false); navigate(`/family-office/families/${fam.id}?rapport=${reportModalType}`); }}
                    className="w-full text-left border border-[#E8E8E8] rounded-lg px-4 py-3 hover:border-[#C9A961] hover:bg-[#F9F7F4] transition-all"
                  >
                    <p className="text-sm font-medium text-[#1A1A1A]">{fam.label || "Famille"}</p>
                    <p className="text-xs text-[#999] mt-0.5">
                      {fam.clientCount || 0} membre{(fam.clientCount || 0) > 1 ? "s" : ""}
                      {fam.client_name ? ` · ${fam.client_name}` : ""}
                    </p>
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setShowReportModal(false)} className="mt-4 w-full text-sm text-[#666] hover:text-[#1A1A1A]">
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}